import React, { useState } from 'react';
import styles from './bowtie-meatball-menu.scss';
import { withCallsToActions } from 'app/area/case/features';
import { HeaderPanelProps } from 'app/components';
import { AddNewConnectedCasesDropdown } from 'app/components/add-new-cases-dropdown/add-new-connected-cases-dropdown';
import { AddNewConnectedCasesWireData } from 'app/area/case/features/calls-to-actions/types';
import { useSharedState } from 'app/infrastructure/redux/hooks';
import { UserType } from 'app/types-shared';
import { MeatballMenu } from 'ui/common/dropdown-menu/meatball-menu';
import { ConnectExistingCases } from 'app/components/connect-existing-cases/connect-existing-cases';
import { useConnectedCases } from 'app/area/bowtie/dropdown/bowtie-meatball-menu-hooks';
import { Favourite } from 'app/area/favourite/favourite';
import { BowtieTranslation } from 'app/area/bowtie/constants';
import { SEQUENCE_DIRECTION } from 'app/area/case/features/connected-cases/sequence';

interface BowtieMeatballMenuProps {
  caseId: number;
  callsToActions?: HeaderPanelProps['callsToActions'];
  loadConnectedCases: (caseNumber: number, connectionType: number) => void;
  translation: BowtieTranslation;
  onChangeCaseConnection?: (params: {
    caseId: number; connectionType: number; connectedCaseId: number; bowtie: boolean;
    instanceKey: string;
  }) => void;
  siblingCaseIds: number[];
  parentCaseId?: number;
  changeSequence: (direction: SEQUENCE_DIRECTION) => void;
  sequenceOrder: number[];
  showChangeSequenceOptions: boolean;
  bowtieOverlayRef?: React.RefObject<HTMLDivElement | null>;
  changeConnectionSequenceId?: number;
  stateForCaseIds: { [caseId: string]: { valid: boolean; fetching: boolean } };
}

export const BowtieMeatBallMenuContainer = (props: BowtieMeatballMenuProps) => {
  const { caseId, callsToActions: fetchCallsToActions, translation, onChangeCaseConnection, changeSequence, siblingCaseIds,
    sequenceOrder, showChangeSequenceOptions, bowtieOverlayRef, changeConnectionSequenceId } = props;
  const [callsToActions, setCallsToActions] = useState(undefined);
  const sharedState = useSharedState();
  const allowForCurrentUser = sharedState?.userType?.v !== UserType.basic;
  const connectionTypes = callsToActions?.connectExistingCases?.options?.map(o => o.v) || [];
  const { connectedCasesByConnectionType, instanceKey, updateBowtieInstance, validateCaseId } = useConnectedCases(caseId, connectionTypes);
  const caseOrder = sequenceOrder?.length ? sequenceOrder : siblingCaseIds;
  const caseIndex = caseOrder?.indexOf(caseId);
  const allowChangeSequence = showChangeSequenceOptions && callsToActions?.editAccess && allowForCurrentUser; 
  const canMoveUp = (): boolean => {
    return allowChangeSequence && (caseIndex > 0);
  };

  const canMoveDown = (): boolean => {
    return allowChangeSequence && (caseIndex < caseOrder?.length - 1);
  };

  const onExpandMenu = async () => {
    if (caseId) {
      const options = await fetchCallsToActions(caseId);
      setCallsToActions(options);
    }
  };

  const renderOption = (direction: SEQUENCE_DIRECTION, text: string) => (
    <li onClick={() => !changeConnectionSequenceId && changeSequence(direction)}
      className={changeConnectionSequenceId ? styles.disabledDropdownOption : undefined}>
      <div className="dropdown-item">{text}</div>
    </li>
  );

  const changeCaseConnection = async (params) => {
    const response = await onChangeCaseConnection({...params, bowtie: true, instanceKey});
    updateBowtieInstance(instanceKey, caseId, connectionTypes);
    return response;
  };

  const renderMenuOptions =
  (allowForCurrentUser && callsToActions?.addNewConnectedCases?.length) ||
  (callsToActions?.connectExistingCases && callsToActions?.editAccess && connectedCasesByConnectionType) ||
  canMoveDown() ||
  canMoveUp();
  const renderDropdown = <div className={styles.bowtieDropdownContent}>
    <ul>
      {allowForCurrentUser &&
        callsToActions?.addNewConnectedCases?.map((addNewConnectedCase: AddNewConnectedCasesWireData, index: number) => {
          return <AddNewConnectedCasesDropdown
            entityId={caseId}
            addNewConnectedCaseButtons={addNewConnectedCase.newCaseButtons}
            textLabelMenu={addNewConnectedCase.label}
            key={index}
            returnToModalParent
          />;
        })}
      {(callsToActions?.connectExistingCases && callsToActions?.editAccess) && connectedCasesByConnectionType &&
        <ConnectExistingCases
          {...translation}
          {...props}
          caseId={caseId}
          options={callsToActions.connectExistingCases?.options}
          label={callsToActions.connectExistingCases?.label}
          connectedCasesIdsByConnectionType={connectedCasesByConnectionType}
          allowWarnings={false}
          searchEnabled
          favouriteComponent={Favourite}
          onChangeCaseConnection={changeCaseConnection}
          onCloseCallback={() => updateBowtieInstance(instanceKey, caseId, connectionTypes)}
          disconnectWarning
          validateCaseId={(caseIdToConnect) => validateCaseId(instanceKey, caseIdToConnect)}
        />
      }
      {canMoveDown() && renderOption(SEQUENCE_DIRECTION.DOWN, translation.textMoveDown)}
      {canMoveUp() && renderOption(SEQUENCE_DIRECTION.UP, translation.textMoveUp)}
    </ul>

    {!renderMenuOptions && (
      <li className={styles.disabledDropdownOption}>
        <div className="dropdown-item">{translation.textOptionsNotFound}</div>
      </li>
    )}
  </div>;
  return (
    <MeatballMenu onExpandMenu={onExpandMenu} portalTarget={bowtieOverlayRef?.current}>
      {renderDropdown}
    </MeatballMenu>
  );
};
export const BowtieMeatBallMenu = withCallsToActions(BowtieMeatBallMenuContainer);
